import type { ActionExecutor } from "../TriggerEngine.interfaces";
import { ITypeMedia } from "../../interfaces/Artists";

export const playMedia: ActionExecutor = async (
  radio,

  config,

  { emitAction, eventContext },
) => {
  const { scene: sceneId, areaId } = config;

  if (!sceneId || !areaId) {
    throw new Error("A target scene and media area are required");
  }

  const { openDatabase, saveSnapshotDatabase } = await import("../../Utils/db");

  const db = await openDatabase();

  const scenes = await db.scenes.toArray();

  const target = scenes.find((s) => s.id === sceneId);

  if (!target) throw new Error("The target scene no longer exists");

  // Find the area and set playing

  const area = target.model.areas.find((a) => a.id === areaId);

  const playResource = area?.media?.resource as
    | { settings?: { playing?: boolean; timeline?: number } }
    | undefined;

  if (
    !playResource?.settings ||
    (area?.media.resource?.type !== ITypeMedia.video &&
      area?.media.resource?.type !== ITypeMedia.videoExternal)
  ) {
    throw new Error("The target playable media no longer exists");
  }

  if (!target.active) {
    for (const scene of scenes) {
      await db.scenes.update(scene.id, { active: scene.id === sceneId });
    }

    await radio.switchScene(target);
  }

  playResource.settings.playing = true;

  playResource.settings.timeline = 0;

  await db.scenes.update(target.id, { model: target.model });

  await radio.updateTemplate(target.model, { eventContext });

  emitAction("studio_refreshed", { model: target.model });

  // Also play the DOM video element if it exists

  const video = document.querySelector(
    `video#${areaId}`,
  ) as HTMLVideoElement | null;

  if (video) {
    try {
      video.currentTime = 0;

      await video.play();
    } catch {
      // autoplay may be blocked
    }
  }

  await saveSnapshotDatabase();
};

export const pauseMedia: ActionExecutor = async (
  radio,

  config,

  { emitAction, eventContext },
) => {
  const { scene: sceneId, areaId } = config;

  if (!sceneId || !areaId) {
    throw new Error("A target scene and media area are required");
  }

  const { openDatabase, saveSnapshotDatabase } = await import("../../Utils/db");

  const db = await openDatabase();

  const scenes = await db.scenes.toArray();

  const target = scenes.find((s) => s.id === sceneId);

  if (!target) throw new Error("The target scene no longer exists");

  const area = target.model.areas.find((a) => a.id === areaId);

  const pauseResource = area?.media?.resource as
    | { settings?: { playing?: boolean } }
    | undefined;

  if (
    !pauseResource?.settings ||
    (area?.media.resource?.type !== ITypeMedia.video &&
      area?.media.resource?.type !== ITypeMedia.videoExternal)
  ) {
    throw new Error("The target playable media no longer exists");
  }

  pauseResource.settings.playing = false;

  await db.scenes.update(target.id, { model: target.model });

  if (target.active) {
    await radio.updateTemplate(target.model, { eventContext });

    emitAction("studio_refreshed", { model: target.model });

    const video = document.querySelector(
      `video#${areaId}`,
    ) as HTMLVideoElement | null;

    if (video) {
      video.pause();
    }
  }

  await saveSnapshotDatabase();
};

export const changeText: ActionExecutor = async (
  radio,

  config,

  { emitAction, eventContext },
) => {
  const { scene: sceneId, widgetId, text } = config;

  if (!sceneId || !widgetId || text === undefined) {
    throw new Error(
      "A target scene, text element and replacement text are required",
    );
  }

  const { openDatabase, saveSnapshotDatabase } = await import("../../Utils/db");

  const db = await openDatabase();

  const scenes = await db.scenes.toArray();

  const target = scenes.find((s) => s.id === sceneId);

  if (!target) throw new Error("The target scene no longer exists");

  const element = target.model.elements?.find((e) => e.id === widgetId);

  if (!element || element.type !== "text") {
    throw new Error("The target text element no longer exists");
  }

  element.options.text = text;

  await db.scenes.update(target.id, { model: target.model });

  if (target.active) {
    await radio.updateTemplate(target.model, { eventContext });

    emitAction("studio_refreshed", { model: target.model });
  }

  await saveSnapshotDatabase();
};
